import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faIdCard,
  faStamp,
  faUsers,
  faHeadset,
  faUserShield,
  faClipboardList,
  faServer,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";
import "./admin.css";

const ROLE_LABEL = {
  super_admin:  "Super Admin",
  kyc_verifier: "KYC Verifier",
  property_verifier: "Property Verifier",
  support:      "Support",
  technician:   "Technician",
};

function NavItem({ to, icon, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `adm-nav-item${isActive ? " active" : ""}`}
    >
      <FontAwesomeIcon icon={icon} />
      {label}
    </NavLink>
  );
}

export default function AdminLayout({ children, pageTitle }) {
  const navigate  = useNavigate();
  const role      = localStorage.getItem("adminRole") || "";
  const firstname = localStorage.getItem("adminFirstname") || "Admin";
  const lastname  = localStorage.getItem("adminLastname")  || "";
  const initials  = `${firstname[0] || "A"}${lastname[0] || ""}`.toUpperCase();

  const handleLogout = () => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      fetch("/api/admin/auth/logout", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    ["adminToken","adminRole","adminFirstname","adminLastname","adminEmail"].forEach(
      (k) => localStorage.removeItem(k)
    );
    navigate("/admin/login", { replace: true });
  };

  const can = (...roles) => roles.includes(role);

  return (
    <div className="adm-root adm-layout">
      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <aside className="adm-sidebar">
        <div className="adm-sidebar-brand">
          <div style={{
            width: 34, height: 34, borderRadius: 8,
            background: "linear-gradient(135deg,#0f2747,#1f3f67)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#f06c24", fontWeight: 900, fontSize: ".85rem",
          }}>
            GB
          </div>
          <div>
            <span>GharBhada</span>
            <small>Admin Panel</small>
          </div>
        </div>

        {/* All roles */}
        <div className="adm-sidebar-section">
          <div className="adm-sidebar-section-label">Overview</div>
          <NavItem to="/admin/dashboard" icon={faChartPie} label="Dashboard" />
        </div>

        {/* KYC verifier + super admin */}
        {can("super_admin", "kyc_verifier") && (
          <div className="adm-sidebar-section">
            <div className="adm-sidebar-section-label">KYC</div>
            <NavItem to="/admin/kyc"   icon={faIdCard}  label="KYC Queue" />
          </div>
        )}

        {can("super_admin", "property_verifier") && (
          <div className="adm-sidebar-section">
            <div className="adm-sidebar-section-label">Property Trust</div>
            <NavItem to="/admin/property-verifications" icon={faStamp} label="Property Queue" />
          </div>
        )}

        {/* Support + super admin */}
        {can("super_admin", "support") && (
          <div className="adm-sidebar-section">
            <div className="adm-sidebar-section-label">Management</div>
            <NavItem to="/admin/users"   icon={faUsers}    label="Users" />
            <NavItem to="/admin/properties" icon={faStamp} label="Properties" />
            <NavItem to="/admin/tickets" icon={faHeadset}  label="Support Tickets" />
          </div>
        )}

        {can("property_verifier") && (
          <div className="adm-sidebar-section">
            <div className="adm-sidebar-section-label">Management</div>
            <NavItem to="/admin/properties" icon={faStamp} label="Properties" />
          </div>
        )}

        {/* Team management — super_admin only */}
        {can("super_admin") && (
          <div className="adm-sidebar-section">
            <div className="adm-sidebar-section-label">Team</div>
            <NavItem to="/admin/team" icon={faUserShield} label="Admin Team" />
          </div>
        )}

        {/* Logs — super_admin + technician */}
        {can("super_admin", "technician") && (
          <div className="adm-sidebar-section">
            <div className="adm-sidebar-section-label">System</div>
            <NavItem to="/admin/logs"   icon={faClipboardList} label="Audit Logs" />
            <NavItem to="/admin/system" icon={faServer}        label="System Info" />
          </div>
        )}

        {/* Footer */}
        <div className="adm-sidebar-footer">
          <div className="adm-sidebar-admin-info">
            <div className="adm-sidebar-avatar">{initials}</div>
            <div>
              <div className="adm-sidebar-name">{firstname} {lastname}</div>
              <div className="adm-sidebar-role">{ROLE_LABEL[role] || role}</div>
            </div>
          </div>
          <button className="adm-logout-btn" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────────────────────── */}
      <div className="adm-main">
        <header className="adm-topbar">
          <div className="adm-topbar-title">{pageTitle || "Admin Panel"}</div>
          <div className="adm-topbar-right">
            <span
              className={`adm-badge adm-role-badge ${role}`}
              style={{ fontSize: ".72rem", padding: ".25rem .65rem" }}
            >
              {ROLE_LABEL[role] || role}
            </span>
          </div>
        </header>

        <main className="adm-content">{children}</main>
      </div>
    </div>
  );
}
